import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Breadcrumbs } from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { EntryRenderer } from "@/components/pages-cms/EntryRenderer"
import { SectionOrderHint, useSectionOrder } from "@/components/pages-cms/useSectionOrder"
import { LanguageToggle } from "@/components/pages-cms/LanguageToggle"
import type { Entry, GalleryCategoriesEntry, Lang, LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"
import { SiteForm, RoomsSection, type SiteData } from "./SiteForm"

type Props = PageProps & {
  site: SiteData & { id: number }
  public_url: string
  page: { key: string; label: string; path: string; reorderable: boolean; custom_order: boolean } | null
  content_entries: Entry[]
  ai_enabled: boolean
  gallery_entry: GalleryCategoriesEntry
  photo_library: LibraryPhoto[]
}

function DangerZone({ site }: { site: { id: number; name: string } }) {
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  function handleRemove() {
    router.delete(`/admin/website/locations/${site.id}`)
    setConfirmOpen(false)
  }

  return (
    <section className="mt-12 rounded-lg border border-danger/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-ink-display">Remove this location</p>
          <p className="mt-0.5 text-xs text-ink-muted">
            Removes {site.name}, its rooms, and its photo placements. Photos stay in the Photos
            library.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          className="text-danger-display hover:border-danger hover:bg-danger-faded"
          onClick={() => setConfirmOpen(true)}
        >
          Remove location
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Remove {site.name}?</DialogTitle>
            <DialogDescription>
              This removes the location, its rooms, and its photo placements from the public site.
              Photos stay in the Photos library. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRemove}>
              Remove location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

export default function LocationEdit() {
  const { props } = usePage<Props>()
  const { site, public_url, page, content_entries, ai_enabled, gallery_entry, photo_library } = props
  const { orderedEntries, moveHandlers, resetOrder } = useSectionOrder(
    page?.key ?? null,
    content_entries,
    page?.reorderable ?? false,
  )
  const flash = props.flash
  const [lang, setLang] = React.useState<Lang>("en")

  // Most sections are the page's bands, in the admin's order; the registry
  // marks the few that belong below the gallery instead.
  const bandEntries = orderedEntries.filter((e) => !("after_gallery" in e && e.after_gallery))
  const tailEntries = orderedEntries.filter((e) => "after_gallery" in e && e.after_gallery)

  const title = page?.label ?? site.name
  const description = `Edit ${title}: location details, rooms, page content, and gallery.`

  return (
    <AdminShell>
      <Head title={`Website Pages — ${title}`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`Website Pages — ${title}`} />
        <meta property="og:description" content={description} />
      </Head>

      <Breadcrumbs
        items={[
          { label: "Website Pages", href: "/admin/website" },
          { label: "Sanctuary Therapy Centre", href: "/admin/website?brand=sanctuary" },
          { label: title },
        ]}
      />

      <PageHeader
        title={title}
        description={`Location details, rooms, page content, and gallery — everything for ${public_url}`}
        actions={
          <Button variant="secondary" asChild>
            <a href={public_url} target="_blank" rel="noopener">
              View page
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </a>
          </Button>
        }
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      <p className="mt-4 text-sm text-ink-muted">
        Every box below saves on its own: change one and a tick and a cross appear under it — the
        tick keeps that change, the cross puts it back. Photos save the moment you choose them.
      </p>

      <div className="max-w-3xl">
        {/* Location record */}
        <section id="details" className="border-b border-hairline pb-12 pt-8">
          <h2 className="mb-1">Location record</h2>
          <p className="mb-4 text-sm text-ink-muted">
            Address, status, features, and description — shown on /{site.slug}.
          </p>
          <SiteForm site={site} actionUrl={`/admin/website/locations/${site.id}`} method="patch" />
        </section>

        {/* Rooms */}
        <section id="rooms" className="border-b border-hairline pb-12 pt-8">
          <h2 className="mb-4">Rooms</h2>
          <RoomsSection siteId={site.id} rooms={site.rooms ?? []} photoLibrary={photo_library} />
        </section>

        {/* Page content */}
        {content_entries.length > 0 ? (
          <>
            <div className="pt-8">
              <LanguageToggle
                lang={lang}
                onChange={setLang}
                staleCount={content_entries.filter((e) => "es_stale" in e && e.es_stale).length}
              />
            </div>
            {page?.reorderable && <SectionOrderHint customOrder={page.custom_order} onReset={resetOrder} />}
            {bandEntries.map((entry) => (
              <EntryRenderer
                key={entry.id}
                entry={entry}
                library={photo_library}
                lang={lang}
                aiEnabled={ai_enabled}
                move={moveHandlers(entry)}
              />
            ))}
          </>
        ) : (
          <section className="border-b border-hairline pb-12 pt-8">
            <h2 className="mb-4">Page content</h2>
            <p className="text-sm text-ink-muted">
              This location's page could not be found in the registry — its rooms and gallery still appear on
              /{site.slug}.
            </p>
          </section>
        )}

        {/* Gallery */}
        <div id="gallery">
          <EntryRenderer entry={gallery_entry} library={photo_library} />
        </div>

        {/* Sections edited below the gallery — the footer, which is the
            bottom of the location's pages. */}
        {tailEntries.map((entry) => (
          <EntryRenderer
            key={entry.id}
            entry={entry}
            library={photo_library}
            lang={lang}
            aiEnabled={ai_enabled}
            move={moveHandlers(entry)}
          />
        ))}

        <DangerZone site={{ id: site.id, name: site.name }} />
      </div>
    </AdminShell>
  )
}
